import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FeatureService } from '../DynamicEntities/admin/services/FeatureService';
import { DynamicFieldRenderer } from '../DynamicEntities/ui/DynamicFieldRenderer';
import { DynamicEntitiesApi } from '../DynamicEntities/core/api/DynamicEntitiesApi';
import { useOptionalDynamicEntitiesContext } from '../DynamicEntities/core/DynamicEntitiesProvider';
import { useBulkDynamicEdit } from './useBulkDynamicEdit';
import type { BulkUpdateResult, EditorPhase } from './MasterCrud.types';
import type { Feature, FeatureEntity } from '../DynamicEntities/admin/services/FeatureService';
import type { DynamicField } from '../DynamicEntities/core/types/DynamicField.types';
import type { GenericList } from '../DynamicEntities/core/types/DynamicEntity.types';

interface EntityWithFields {
  featureEntity: FeatureEntity;
  fields: DynamicField[];
  lists: GenericList[];
}

interface MasterCrudBulkDynamicEditorProps {
  featureCode: string;
  recordIds: string[];
  onClose: () => void;
  onComplete: (result: BulkUpdateResult) => void;
  /**
   * Función de traducción inyectada por el padre (MasterCrud). Si no se provee,
   * se usan los textos por defecto en español.
   */
  t?: (key: string, defaultValue?: string) => string;
}

export const MasterCrudBulkDynamicEditor: React.FC<MasterCrudBulkDynamicEditorProps> = ({
  featureCode,
  recordIds,
  onClose,
  onComplete,
  t = (_key: string, defaultValue?: string) => defaultValue ?? _key,
}) => {
  const ctx = useOptionalDynamicEntitiesContext();
  const baseUrl = ctx?.baseUrl ?? '';
  const fetcher = ctx?.fetcher;

  const [loadingFeature, setLoadingFeature] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [entitiesWithFields, setEntitiesWithFields] = useState<EntityWithFields[]>([]);

  // fieldValues: { entityCode -> { fieldCode -> value } }
  const [fieldValues, setFieldValues] = useState<Record<string, Record<string, unknown>>>({});

  const [modalPhase, setModalPhase] = useState<EditorPhase>('loading');

  const [typedConfirmationValue, setTypedConfirmationValue] = useState('');
  const [isTypedConfirmModalOpen, setIsTypedConfirmModalOpen] = useState(false);
  const expectedConfirmationKeyword = useMemo(() => `update-bulk-${featureCode}`, [featureCode]);
  const isTypedConfirmationValid = typedConfirmationValue === expectedConfirmationKeyword;

  const handleBulkComplete = useCallback(
    (result: BulkUpdateResult) => {
      onComplete(result);
    },
    [onComplete]
  );

  const { phase, progress, result, start, cancel } = useBulkDynamicEdit({
    baseUrl,
    fetcher,
    featureCode,
    recordIds,
    onComplete: handleBulkComplete,
  });

  // Sync useBulkDynamicEdit phase into local modalPhase (except loading)
  useEffect(() => {
    if (modalPhase !== 'loading' && modalPhase !== 'confirming') {
      setModalPhase(phase);
    }
  }, [phase]);

  // Load feature + entity definitions
  useEffect(() => {
    if (!baseUrl) return;

    const load = async () => {
      setLoadingFeature(true);
      setLoadError(null);
      try {
        const featureService = new FeatureService(baseUrl, fetcher);
        const feature: Feature = await featureService.getByPath(featureCode);

        if (!feature.featureEntities?.length) {
          setEntitiesWithFields([]);
          setModalPhase('editing');
          return;
        }

        // Load definition for each entity (to get fields + lists)
        const entities: EntityWithFields[] = [];
        for (const fe of feature.featureEntities) {
          if (!fe.dynamicEntity) continue;
          // Filter: exclude inactive
          if (fe.dynamicEntity.isDisabled) continue;

          try {
            const def = await DynamicEntitiesApi.getDefinition(baseUrl, featureCode, fe.dynamicEntity.code, fetcher);
            entities.push({
              featureEntity: fe,
              fields: def.fields.filter(f => f.visible),
              lists: def.lists,
            });
          } catch {
            // skip entities that fail to load
          }
        }

        setEntitiesWithFields(entities);
        setModalPhase('editing');
      } catch (err: any) {
        setLoadError(err?.message ?? t('bulkLoadError', 'Error al cargar las entidades dinámicas'));
        setModalPhase('loading');
      } finally {
        setLoadingFeature(false);
      }
    };

    load();
  }, [baseUrl, fetcher, featureCode]);

  const handleFieldChange = useCallback(
    (entityCode: string, fieldCode: string, value: unknown) => {
      setFieldValues(prev => ({
        ...prev,
        [entityCode]: {
          ...(prev[entityCode] ?? {}),
          [fieldCode]: value,
        },
      }));
    },
    []
  );

  const buildPatches = useCallback(() => {
    return Object.entries(fieldValues)
      .map(([entityCode, fv]) => {
        const defined = Object.fromEntries(
          Object.entries(fv).filter(([, v]) => v !== undefined)
        );
        return { entityCode, fieldValues: defined };
      })
      .filter(patch => Object.keys(patch.fieldValues).length > 0);
  }, [fieldValues]);

  const countFilledFields = useCallback(() => {
    let count = 0;
    for (const fv of Object.values(fieldValues)) {
      for (const v of Object.values(fv)) {
        if (v !== undefined && v !== '') count++;
      }
    }
    return count;
  }, [fieldValues]);

  const handlePreview = useCallback(() => {
    if (countFilledFields() === 0) return;
    setModalPhase('confirming');
  }, [countFilledFields]);

  const handleConfirm = useCallback(async () => {
    const patches = buildPatches();
    setModalPhase('processing');
    await start({ entityPatches: patches });
  }, [buildPatches, start]);

  const handleBackToEdit = useCallback(() => {
    setModalPhase('editing');
    setTypedConfirmationValue('');
    setIsTypedConfirmModalOpen(false);
  }, []);

  const handleOpenTypedConfirm = useCallback(() => {
    setTypedConfirmationValue('');
    setIsTypedConfirmModalOpen(true);
  }, []);

  const handleCancelTypedConfirm = useCallback(() => {
    setIsTypedConfirmModalOpen(false);
    setTypedConfirmationValue('');
  }, []);

  const handleConfirmTypedConfirm = useCallback(async () => {
    if (!isTypedConfirmationValid) return;
    setIsTypedConfirmModalOpen(false);
    await handleConfirm();
  }, [isTypedConfirmationValid, handleConfirm]);

  const handleCancel = useCallback(() => {
    cancel();
    onClose();
  }, [cancel, onClose]);

  const exportErrors = useCallback(() => {
    if (!result?.errors?.length) return;
    const lines = [`${t('recordIdCsv', 'ID del Registro')},${t('reasonCsv', 'Motivo')}`, ...result.errors.map(e => `${e.recordId},"${e.reason.replace(/"/g, '""')}"`).join('\n')];
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `errores_edicion_masiva_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [result]);

  const progressPercent =
    progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl bg-white dark:bg-dark-bg-primary rounded-xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('bulkEditTitle', 'Editar campos dinámicos')}
          </h2>
          {modalPhase !== 'processing' && (
            <button
              type="button"
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label={t('close', 'Cerrar')}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* FASE: loading */}
          {modalPhase === 'loading' && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              {loadingFeature && (
                <>
                  <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('bulkLoadingFields', 'Cargando campos dinámicos...')}</p>
                </>
              )}
              {loadError && (
                <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-center">
                  {loadError}
                </div>
              )}
            </div>
          )}

          {/* FASE: editing */}
          {modalPhase === 'editing' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('bulkEditingIntroPrefix', 'Se aplicarán cambios a')} <strong>{recordIds.length}</strong>{' '}
                {t('bulkEditingIntroSuffix', 'registros seleccionados. Llena solo los campos que deseas modificar; los campos vacíos no se alterarán.')}
              </p>

              {entitiesWithFields.length === 0 && (
                <div className="text-sm text-gray-400 italic text-center py-8">
                  {t('bulkNoDynamicEntities', 'No hay entidades dinámicas configuradas para este módulo.')}
                </div>
              )}

              {entitiesWithFields.map(({ featureEntity, fields, lists }) => {
                const entity = featureEntity.dynamicEntity!;
                const isReadonlyOrDisabled = entity.isReadonly || entity.isDisabled;

                return (
                  <div key={entity.code} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {entity.description || entity.code}
                      </h3>
                      {entity.multiRecord && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                          {t('bulkMultiRecordNote', 'Solo se actualizará la fila principal de cada registro')}
                        </p>
                      )}
                      {isReadonlyOrDisabled && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {t('bulkReadonlyEntity', 'Esta entidad es de solo lectura')}
                        </p>
                      )}
                    </div>
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                      {fields.map(field => {
                        const entityCode = entity.code;
                        const value = fieldValues[entityCode]?.[field.code];
                        const listOptions = field.genericListId
                          ? (lists.find(l => l.id === field.genericListId)?.items ?? []).map(i => ({
                            label: i.label || i.value,
                            value: i.value,
                          }))
                          : [];

                        return (
                          <DynamicFieldRenderer
                            key={field.code}
                            field={{
                              ...field,
                              disabled: isReadonlyOrDisabled || field.disabled || field.readonly,
                            }}
                            value={value}
                            onChange={v => handleFieldChange(entityCode, field.code, v)}
                            options={listOptions}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* FASE: confirming */}
          {modalPhase === 'confirming' && (
            <div className="space-y-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  {t('bulkChangesSummary', 'Resumen de cambios')}
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  {t('bulkWillApply', 'Se aplicarán')}{' '}
                  <strong>{countFilledFields()} {t('bulkFieldsLabel', 'campo(s)')}</strong> {t('bulkToLabel', 'a')}{' '}
                  <strong>{recordIds.length} {t('bulkRecordsLabel', 'registro(s)')}</strong>.
                </p>
              </div>

              <div className="space-y-3">
                {buildPatches().map(patch => (
                  <div key={patch.entityCode} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      {entitiesWithFields.find(e => e.featureEntity.dynamicEntity?.code === patch.entityCode)?.featureEntity.dynamicEntity?.description || patch.entityCode}
                    </p>
                    <ul className="space-y-1">
                      {Object.entries(patch.fieldValues).map(([fCode, fVal]) => {
                        const entityDef = entitiesWithFields.find(e => e.featureEntity.dynamicEntity?.code === patch.entityCode);
                        const fieldDef = entityDef?.fields.find(f => f.code === fCode);
                        const label = fieldDef?.labels['es'] || fCode;
                        return (
                          <li key={fCode} className="text-sm text-gray-700 dark:text-gray-300 flex gap-2">
                            <span className="font-medium">{label}:</span>
                            <span className="text-gray-500 dark:text-gray-400 truncate">
                              {fVal === null ? `(${t('emptyValue', 'vacío')})` : String(fVal)}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>

            </div>
          )}

          {/* FASE: processing */}
          {modalPhase === 'processing' && (
            <div className="flex flex-col items-center justify-center py-8 gap-6">
              <div className="w-full">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <span>{t('bulkProcessing', 'Procesando registros...')}</span>
                  <span>{progress.processed} / {progress.total}</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-primary-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span className="text-green-600 dark:text-green-400">{progress.succeeded} {t('bulkSucceededLabel', 'exitosos')}</span>
                  {progress.failed > 0 && (
                    <span className="text-red-600 dark:text-red-400">{progress.failed} {t('bulkFailedLabel', 'fallidos')}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* FASE: completed */}
          {modalPhase === 'completed' && result && (
            <div className="space-y-4">
              <div className={`rounded-lg p-4 border ${result.failed === 0
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                }`}>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                  {t('bulkOperationCompleted', 'Operación completada')}
                </p>
                <div className="flex gap-6 text-sm">
                  <span className="text-green-700 dark:text-green-300 font-medium">
                    {result.succeeded} {t('bulkSucceededLabel', 'exitosos')}
                  </span>
                  {result.failed > 0 && (
                    <span className="text-red-700 dark:text-red-300 font-medium">
                      {result.failed} {t('bulkFailedLabel', 'fallidos')}
                    </span>
                  )}
                  <span className="text-gray-500 dark:text-gray-400">
                    {t('bulkOfLabel', 'de')} {result.total} {t('bulkRecordsPlural', 'registros')}
                  </span>
                </div>
              </div>

              {result.errors && result.errors.length > 0 && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      {t('bulkRecordsWithError', 'Registros con error')}
                    </p>
                  </div>
                  <div className="max-h-40 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                    {result.errors.map(err => (
                      <div key={err.recordId} className="px-4 py-2 text-sm">
                        <span className="font-mono text-xs text-gray-500 dark:text-gray-400 mr-2">{err.recordId}</span>
                        <span className="text-red-600 dark:text-red-400">{err.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">

          {modalPhase === 'editing' && (
            <>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('cancel', 'Cancelar')}
              </button>
              <button
                type="button"
                onClick={handlePreview}
                disabled={countFilledFields() === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {t('bulkPreview', 'Previsualizar')}
              </button>
            </>
          )}

          {modalPhase === 'confirming' && (
            <>
              <button
                type="button"
                onClick={handleBackToEdit}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('back', 'Volver')}
              </button>
              <button
                type="button"
                onClick={handleOpenTypedConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
              >
                {t('bulkApplyTo', 'Aplicar a')} {recordIds.length} {t('bulkRecordsPlural', 'registros')}
              </button>
            </>
          )}

          {modalPhase === 'processing' && (
            <button
              type="button"
              onClick={cancel}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
            >
              {t('bulkCancelOperation', 'Cancelar operación')}
            </button>
          )}

          {modalPhase === 'completed' && (
            <>
              {result && result.errors && result.errors.length > 0 && (
                <button
                  type="button"
                  onClick={exportErrors}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('bulkExportErrorsCsv', 'Exportar errores CSV')}
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
              >
                {t('close', 'Cerrar')}
              </button>
            </>
          )}

        </div>
      </div>

      {/* Modal de confirmación tipeada (overlay encima del editor) */}
      {isTypedConfirmModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md bg-white dark:bg-dark-bg-primary rounded-xl shadow-2xl flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
              <h3 className="text-base font-semibold text-red-800 dark:text-red-200">
                {t('bulkConfirmTitle', 'Confirmar modificación masiva')}
              </h3>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t('bulkConfirmLine1Prefix', 'Vas a modificar')} <strong>{recordIds.length}</strong>{' '}
                {t('bulkConfirmLine1Suffix', 'registro(s). Esta acción no se puede deshacer.')}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t('bulkConfirmTypePrompt', 'Para confirmar, escribe')}{' '}
                <code className="font-mono text-xs bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 px-1.5 py-0.5 rounded select-all">
                  {expectedConfirmationKeyword}
                </code>
              </p>
              <input
                type="text"
                value={typedConfirmationValue}
                onChange={e => setTypedConfirmationValue(e.target.value)}
                autoComplete="off"
                autoFocus
                spellCheck={false}
                aria-label={t('bulkConfirmAriaLabel', 'Confirmación de modificación masiva')}
                className="w-full px-3 py-2 text-sm font-mono bg-white dark:bg-dark-bg-primary border border-red-300 dark:border-red-700 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleCancelTypedConfirm}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('cancel', 'Cancelar')}
              </button>
              <button
                type="button"
                onClick={handleConfirmTypedConfirm}
                disabled={!isTypedConfirmationValid}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {t('confirmText', 'Confirmar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
